import * as types from './actionTypes';
import * as layerTypes from '../layer/actionTypes';
import * as userTypes from '../user/actionTypes';

const normalizeId = value => String(value || '').trim();

const getObservationIdFromSelectedItem = item =>
  normalizeId(
    (item && item.observationId) ||
      (item && item.ObservationId) ||
      (item && item.EntityId) ||
      (item && item.id) ||
      (item && item.properties && item.properties.observationId) ||
      (item && item.properties && item.properties.ObservationId) ||
      (item && item.properties && item.properties.EntityId)
  );

const getTrackObservationIds = selectedTrackObservations =>
  new Set(
    (Array.isArray(selectedTrackObservations) ? selectedTrackObservations : [])
      .map(item =>
        normalizeId(
          item &&
            item.Observation &&
            (item.Observation.observationId || item.Observation.EntityId)
        )
      )
      .filter(Boolean)
  );

const getResultStableId = item =>
  normalizeId(item && (item.id || item.uri || item.documentUri));

const dedupeResultsById = items => {
  const list = Array.isArray(items) ? items : [];
  const seen = new Set();
  const deduped = [];
  list.forEach(item => {
    const stableId = getResultStableId(item);
    if (!stableId) {
      deduped.push(item);
      return;
    }
    if (seen.has(stableId)) return;
    seen.add(stableId);
    deduped.push(item);
  });
  return deduped;
};

const reducer = (
  state = {
    fetchingList: false,
    aggregatesFetching: false,
    selectedItem: {},
    selectedTrackObservations: [],
    results: [],
    total: 0,
    dataSources: [],
    agents: [],
    scrollToTop: false,
    mounted: false,
    facets: {},
    alerts: { results: [], start: 0 },
    resultTypeCounts: null,
    allowAlertSounds: false, // initialize alerts here
    audio: new Audio('FocusAlert.wav'),
    viewedObservationIds: {},
    viewedTrackIds: {},
    trackObservationCounts: {}
  },
  action
) => {
  switch (action.type) {
    case types.SET_AGGREGATES_FETCHING:
      return {
        ...state,
        aggregatesFetching: action.payload
      };
    case types.SET_AGGREGATES:
      return {
        ...state,
        aggregatesFetching: false,
        aggregates: action.payload
      };
    case types.START_TIMELINE_FETCH:
      return {
        ...state,
        fetchingList: true
      };
    case types.STOP_TIMELINE_FETCH:
      return {
        ...state,
        fetchingList: false
      };
    case types.SET_TIMELINE_SUCCESS:
      if (
        (action.payload && action.payload._replace) ||
        Number(action.payload && action.payload.start) <= 1
      ) {
        return {
          ...state,
          ...action.payload,
          results: dedupeResultsById(
            (action.payload && action.payload.results) || []
          ),
          facets:
            (action.payload && action.payload.facets) || state.facets || {},
          fetchingList: false
        };
      } else {
        const mergedResults = dedupeResultsById(
          state.results.concat(action.payload.results || [])
        );
        return {
          ...state,
          results: mergedResults,
          start: action.payload.start,
          facets:
            (action.payload && action.payload.facets) || state.facets || {},
          fetchingList: false
        };
      }
    case types.SET_ALERTS:
      if (
        state.mounted &&
        action.payload.results.length > state.alerts.results.length &&
        state.allowAlertSounds
      ) {
        state.audio.play();

        let playCount = 1;

        const intervalId = setInterval(() => {
          state.audio.play();
          playCount++;
          if (playCount >= 3) {
            clearInterval(intervalId);
          }
        }, 5000);
      }
      if (Number(action.payload && action.payload.start) === 1) {
        let newState = { ...state, alerts: action.payload, mounted: true };
        return newState;
      } else {
        return {
          ...state,
          alerts: {
            results: state.alerts.results.concat(action.payload.results),
            start: action.payload.start
          },
          fetchingList: false,
          mounted: true
        };
      }
    case types.SET_ALERT_OBSERVATION_MEDIA:
      return {
        ...state,
        alerts: {
          ...state.alerts,
          results: state.alerts.results.map(item => {
            if (item.id === action.payload.observationId) {
              item.extracted.content.map(content => {
                if (content.Observation) {
                  content.Observation.media = action.payload.values;
                }
                return content;
              });
            }
            return item;
          })
        }
      };
    case types.SET_OBSERVATION_MEDIA:
      return {
        ...state,
        results: state.results.map(item => {
          if (item.id === action.payload.observationId) {
            // Find observation object
            item.extracted.content.map(content => {
              if (content.Observation) {
                content.Observation.media = action.payload.values;
              }
              return content;
            });
          }
          return item;
        })
      };
    case types.SET_DATA_SOURCES: {
      const dataSources = action.payload.facets
        ? action.payload.facets.dataSources
          ? action.payload.facets.dataSources.facetValues
            ? action.payload.facets.dataSources.facetValues
            : []
          : []
        : [];
      return {
        ...state,
        dataSources: dataSources
      };
    }
    case types.SET_RESULT_TYPE_COUNTS:
      return {
        ...state,
        resultTypeCounts: action.payload || null
      };
    case types.SET_AGENTS: {
      const agents = action.payload.facets
        ? action.payload.facets.agents
          ? action.payload.facets.agents.facetValues
            ? action.payload.facets.agents.facetValues
            : []
          : []
        : [];
      return {
        ...state,
        agents: agents
      };
    }
    case types.CLEAR_TIMELINE_FACETS:
      return {
        ...state,
        dataSources: [],
        agents: [],
        facets: {}
      };
    case types.CLEAR_TIMELINE_RESULTS:
      return {
        ...state,
        fetchingList: false,
        results: [],
        total: 0,
        start: 0,
        facets: {},
        alerts: { results: [], total: 0, start: 1 },
        selectedItem: {},
        selectedTrackObservations: [],
        trackObservationCounts: {},
        resultTypeCounts: null
      };
    case types.SET_SELECTED_ITEM: {
      let newSelectedItem = {};
      if (
        (state.selectedItem.observationId &&
          action.payload.observationId &&
          state.selectedItem.observationId == action.payload.observationId) ||
        (state.selectedItem.featureId &&
          action.payload.featureId &&
          state.selectedItem.featureId == action.payload.featureId)
      ) {
        newSelectedItem = { ...state.selectedItem, ...action.payload };
      } else {
        newSelectedItem = action.payload;
      }
      const selectedObservationId = getObservationIdFromSelectedItem(
        action.payload
      );
      const isTrackSelection = Boolean(
        (action.payload && action.payload.featureId) ||
          (action.payload &&
            action.payload.properties &&
            action.payload.properties.EntityType === 'track')
      );
      const trackObservationIds = getTrackObservationIds(
        state.selectedTrackObservations
      );
      const shouldKeepSelectedTrackObservations =
        !isTrackSelection &&
        selectedObservationId &&
        trackObservationIds.has(selectedObservationId);
      return {
        ...state,
        selectedItem: newSelectedItem,
        selectedTrackObservations: shouldKeepSelectedTrackObservations
          ? state.selectedTrackObservations
          : []
      };
    }
    case types.MARK_OBSERVATION_VIEWED: {
      if (!action.payload) return state;
      return {
        ...state,
        viewedObservationIds: {
          ...state.viewedObservationIds,
          [action.payload]: true
        }
      };
    }
    case types.MARK_TRACK_VIEWED: {
      if (!action.payload) return state;
      return {
        ...state,
        viewedTrackIds: {
          ...state.viewedTrackIds,
          [action.payload]: true
        }
      };
    }
    case types.SET_SELECTED_OBSERVATION_MEDIA:
      return {
        ...state,
        selectedItem: {
          ...state.selectedItem,
          media: action.payload
        }
      };
    case types.CLEAR_SELECTED_ITEM:
      return {
        ...state,
        selectedItem: {},
        selectedTrackObservations: []
      };
    case layerTypes.SET_TRACK_INFO:
      return {
        ...state,
        results: state.results.map(item => {
          if (
            action.payload.instance &&
            action.payload.instance.GeoFeature &&
            item.id === action.payload.instance.GeoFeature.featureId
          ) {
            // Find observation object
            item.extracted.content.map(content => {
              if (content.GeoFeature) {
                const agentName = content.GeoFeature.agent.agentName;
                const featureType = content.GeoFeature.featureType;
                content.GeoFeature = action.payload.instance.GeoFeature;
                content.GeoFeature.projectName =
                  action.payload.headers.projectName;
                if (content.GeoFeature.agent.agentName == '')
                  content.GeoFeature.agent.agentName = agentName;
                if (featureType) content.GeoFeature.featureType = featureType;
              }
              return content;
            });
          }
          return item;
        }),
        selectedItem: {
          ...state.selectedItem,
          projectName: action.payload.headers.projectName
        }
      };
    case layerTypes.SET_TRACK_OBSERVATIONS:
      return {
        ...state,
        selectedTrackObservations: Array.isArray(action.payload)
          ? action.payload
          : []
      };
    case layerTypes.APPEND_TRACK_OBSERVATIONS: {
      const existing = Array.isArray(state.selectedTrackObservations)
        ? state.selectedTrackObservations
        : [];
      const incoming = Array.isArray(action.payload) ? action.payload : [];
      if (!incoming.length) return state;
      const seenIds = new Set(
        existing.map(
          item => item && item.Observation && item.Observation.observationId
        )
      );
      const merged = existing.slice();
      incoming.forEach(item => {
        const obsId =
          item && item.Observation && item.Observation.observationId;
        if (!obsId || seenIds.has(obsId)) return;
        seenIds.add(obsId);
        merged.push(item);
      });
      return {
        ...state,
        selectedTrackObservations: merged
      };
    }
    case layerTypes.SET_TRACK_OBSERVATION_COUNT: {
      const payload = action.payload || {};
      const trackId = String(payload.trackId || '').trim();
      if (!trackId) return state;
      return {
        ...state,
        trackObservationCounts: {
          ...(state.trackObservationCounts || {}),
          [trackId]: Math.max(0, Number(payload.count) || 0)
        }
      };
    }
    case layerTypes.SET_TRACK_OBSERVATIONS_MEDIA:
      return {
        ...state,
        selectedTrackObservations: state.selectedTrackObservations.map(item => {
          if (
            item &&
            item.Observation &&
            item.Observation.observationId === action.payload.observationId
          ) {
            // Return a new item object with an immutable Observation update,
            // so React detects the change and re-renders the camera badge count.
            return {
              ...item,
              Observation: {
                ...item.Observation,
                media: action.payload.values
              }
            };
          }
          return item;
        })
      };
    case types.SET_SCROLL_TO_TOP:
      return {
        ...state,
        scrollToTop: action.payload
      };
    case types.ALLOW_ALERT_SOUNDS:
      if (state.mounted && state.alerts.results.length > 0) {
        state.audio.play();

        let playCount = 1;

        const intervalId = setInterval(() => {
          state.audio.play();
          playCount++;
          if (playCount >= 3) {
            clearInterval(intervalId);
          }
        }, 5000);
      }
      return {
        ...state,
        allowAlertSounds: true
      };
    case types.DISABLE_ALERT_SOUNDS:
      return {
        ...state,
        allowAlertSounds: false
      };
    case userTypes.LOCAL_LOGOUT:
    case userTypes.NETWORK_LOGOUT_SUCCESS:
      return {
        ...state,
        viewedObservationIds: {},
        viewedTrackIds: {},
        // Was never reset here, so MapComponent's mounted:true->false check
        // (which stops the LKP/countdown timers on logout) could never
        // fire, and the two "session is live" timers kept polling forever
        // - including with a stale/expired session, logging a repeating
        // failed request every 20s.
        mounted: false
      };
    default:
      return state;
  }
};

const aggregates = state => state.aggregates || {};
const aggregatesFetching = state => state.aggregatesFetching || false;
const timelineResults = state => state.results || [];
const timelineTotal = state => state.total || 0;
const timelineFetching = state => state.fetchingList;
const timelineSelectedItem = state => state.selectedItem || {};
const timelineSelectedTrackObservations = state =>
  state.selectedTrackObservations || [];
const trackObservationCounts = state => state.trackObservationCounts || {};
const dataSources = state => state.dataSources || [];
const agents = state => state.agents || [];

const alerts = state => state.alerts || {};
const alertsTotal = state => state.alerts.total || 0;
const alertSounds = state => state.allowAlertSounds || false;
const mounted = state => state.mounted || false;

const timelineSelectors = {
  aggregates,
  aggregatesFetching,
  timelineResults,
  timelineTotal,
  timelineFetching,
  timelineSelectedItem,
  timelineSelectedTrackObservations,
  trackObservationCounts,
  dataSources,
  agents,
  alerts,
  alertsTotal,
  alertSounds,
  mounted
};

export default reducer;
export { timelineSelectors };
